import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import toast from "react-hot-toast";

import type { BuildingOption } from "../../../../api/client/types/common.types";
import { vm } from "../../../../shared/utils/validationMessages";
import { buildReportFileName } from "../../utils/reportFilenames";

const managementYearSummaryFormSchema = z.object({
  year: z
    .string()
    .min(1, { message: vm.YEAR_RANGE })
    .regex(/^\d{4}$/, { message: vm.YEAR_RANGE })
    .refine((value) => {
      const numericYear = Number(value);
      return numericYear >= 2000 && numericYear <= 2100;
    }, {
      message: vm.YEAR_RANGE,
    }),
  building_id: z.string().min(1, { message: vm.REPORT_BUILDING_REQUIRED }),
});

export type ManagementYearSummaryFormValues = z.infer<
  typeof managementYearSummaryFormSchema
>;

interface UseManagementYearSummaryReportGeneratorProps {
  onGenerate: (year: number, building_id: string) => Promise<Blob | null>;
  /** Full list of buildings (all available). Not filtered by date. */
  buildingOptions: BuildingOption[];
}

export const useManagementYearSummaryReportGenerator = ({
  onGenerate,
  buildingOptions,
}: UseManagementYearSummaryReportGeneratorProps) => {
  const form = useForm<ManagementYearSummaryFormValues>({
    resolver: zodResolver(managementYearSummaryFormSchema),
    mode: "onSubmit",
    reValidateMode: "onSubmit",
    defaultValues: {
      year: String(new Date().getFullYear()),
      building_id: "",
    },
  });

  const { watch, handleSubmit, formState } = form;
  const { errors, isValid } = formState;

  const onSubmit = async (data: ManagementYearSummaryFormValues) => {
    const numericYear = Number(data.year);
    if (Number.isNaN(numericYear)) {
      toast.error(vm.YEAR_RANGE);
      return;
    }

    const buildingName =
      buildingOptions.find((b) => b.id === data.building_id)?.name ??
      "Unidad residencial";

    try {
      const blob = await onGenerate(numericYear, data.building_id);
      if (blob !== null) {
        const startDate = `01/01/${numericYear}`;
        const endDate = `31/12/${numericYear}`;

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = buildReportFileName({
          reportType: "management",
          buildingName,
          startDate,
          endDate,
          extension: "pdf",
        });
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
      toast.success(
        "Resumen anual del informe de gestión generado y descargado correctamente"
      );
    } catch (error) {
      console.error("Error generating management yearly summary report:", error);
      toast.error(
        "Error al generar el resumen anual. Por favor, intenta nuevamente."
      );
    }
  };

  return {
    form,
    values: {
      year: watch("year"),
      building_id: watch("building_id"),
    },
    errors: {
      year: errors.year?.message,
      building_id: errors.building_id?.message,
    },
    isValid,
    isSubmitting: formState.isSubmitting,
    handleGenerateReport: handleSubmit(onSubmit),
  };
};

